import { isHungry } from './farmAnimals.mjs';
import { serverNow } from '../state/serverClock.mjs';
// Friends help on each other's farms (spec 2026-09-30-farm-5-visiting-design.md) — the client's mirror of
// app/game/farm_help.py: what a tap on a friend's farm may do, and the texts. Pure.
export const HELPS_PER_DAY = 50;                 // owner 2026-10-27 (was 5)
// A watering cuts a third of the time left, at least 1 h, at most 10 h — never more than is left.
export const waterCutSeconds = (left) => Math.min(left, Math.max(3600, Math.min(36000, left / 3)));
export const PET_BELOW = 80;
const HELP_XP = 2, HELP_COINS = 5;                 // per help, flat (farm_help.HELP_XP / HELP_COINS)

export const REJECT_TOAST = {
  HELP_LIMIT: `Pomogłeś już ${HELPS_PER_DAY} razy dziś {heart}`,
  HELP_NOT_GROWING: 'Tu nie ma czego podlać',
  HELP_NO_ANIMAL: 'Tego zwierzaka tu nie ma',
  HELP_ALREADY: 'Ktoś już tu dziś pomógł {heart}',
  ANIMAL_HAPPY: 'Jest już szczęśliwy {heart}',
  ANIMAL_NOT_HUNGRY: 'Jeszcze nie jest głodne {check}',
  NOT_FRIENDS: 'Tylko znajomi mogą pomagać',
  HELP_NO_KONEWKA: 'Najpierw kup konewkę',
  HELP_BAD_AREA: 'Tak się nie da podlać',
};

export function cellHelp(view, cell, nowMs) {
  if ((view.help_left ?? 0) <= 0) return { ok: false, toast: REJECT_TOAST.HELP_LIMIT };
  const key = `${cell.x},${cell.y}`;
  const c = view.farm?.cells?.[key];
  if (!c || (c.state !== 'planted' && c.state !== 'ready')) return { ok: false, toast: REJECT_TOAST.HELP_NOT_GROWING };
  const ripe = c.state === 'ready' || nowMs - Date.parse(c.planted_at_iso) >= c.grow_seconds * 1000;
  if (ripe) return { ok: false, toast: `Już dojrzało — zbierze ${view.name} {basket}` };
  if ((view.helped_today?.cells ?? []).includes(key)) return { ok: false, toast: REJECT_TOAST.HELP_ALREADY };
  return { ok: true, op: { op: 'water', x: cell.x, y: cell.y } };
}

// A growing crop a watering can still help: planted, not ripe on the clock, not helped today.
const waterable = (view, x, y, nowMs) => {
  const key = `${x},${y}`;
  const c = view.farm?.cells?.[key];
  return !!c && c.state === 'planted' && nowMs - Date.parse(c.planted_at_iso) < c.grow_seconds * 1000
    && !(view.helped_today?.cells ?? []).includes(key);
};

// The Konewka's pour on a 2×2 block (footprint.areaCells): the server waters what it can, `n` of the helps left.
export function areaHelp(view, cells, nowMs) {
  const left = view.help_left ?? 0;
  if (left <= 0) return { ok: false, toast: REJECT_TOAST.HELP_LIMIT };
  const n = cells.filter(({ x, y }) => waterable(view, x, y, nowMs)).length;
  if (!n) return { ok: false, toast: REJECT_TOAST.HELP_NOT_GROWING };
  return { ok: true, op: { op: 'water_area', cells }, n: Math.min(n, left) };
}

// The Konewka's hover (owner 2026-10-27: "see if and how many things I'll water"): the fields a click would
// water — as the server picks them, in row order up to the helps left, less the ones already on their way
// (`reserved`) — and the hint over the block, in the words of the machines' hints on your own farm.
export function areaPreview(view, cells, nowMs, reserved = 0) {
  const left = (view.help_left ?? 0) - reserved;
  if (left <= 0) return { cells: [], hint: REJECT_TOAST.HELP_LIMIT };
  const will = cells.filter(({ x, y }) => waterable(view, x, y, nowMs))
    .sort((a, b) => a.y - b.y || a.x - b.x).slice(0, left);
  if (!will.length) return { cells: [], hint: REJECT_TOAST.HELP_NOT_GROWING };
  const n = will.length;
  return { cells: will, hint: `Podlejesz ${n} ${plural(n, 'pole', 'pola', 'pól')} · +${HELP_COINS * n} {coin}` };
}

// What a watering did, from the server's answer: "Gotowe!" when it all ripened, else the time taken off.
const hm = (s) => {
  const min = Math.round(s / 60), h = Math.floor(min / 60), m = min % 60;
  return h && m ? `${h} h ${m} min` : h ? `${h} h` : `${m} min`;
};
export function cutLabel(watered) {
  if (!watered?.length) return '';
  if (watered.every((w) => w.ripe)) return 'Gotowe!';
  return `−${hm(watered.reduce((s, w) => s + w.cut_s, 0))}`;
}

export function helpFloat(out) {
  const reward = `+${HELP_XP * out.helps} XP  +${HELP_COINS * out.helps} {coin}`;
  const cut = cutLabel(out.watered);
  return cut ? `${cut}\n${reward}` : reward;
}

export function animalHelpItems(view, animal, nowMs = serverNow()) {
  if ((view.helped_today?.animals ?? []).includes(animal.id)) return [];
  const happy = (animal.happiness ?? 0) >= PET_BELOW;
  const hungry = isHungry(animal, nowMs);                 // only a hungry animal is fed (owner 2026-09-30)
  return [
    hungry ? { op: 'feed', label: 'Nakarm', enabled: true } : { op: 'feed', label: 'Najedzona {check}', enabled: false },
    happy ? { op: 'pet', label: 'Jest szczęśliwy {heart}', enabled: false } : { op: 'pet', label: 'Pogłaskaj', enabled: true },
  ];
}

export const helpCounter = (n) => `Pomoc: ${n}/${HELPS_PER_DAY} {heart}`;

const MALE_A = new Set(['Kuba', 'Barnaba', 'Kosma', 'Bonawentura']);
const isFemale = (h) => h.female ?? (/a$/i.test(h.name) && !MALE_A.has(h.name));
const plural = (n, one, few, many) => (n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? few : many);

export function helpersText(helpers) {
  return Object.values(helpers ?? {}).map((h) => {
    const f = isFemale(h);
    const parts = [];
    if (h.water) parts.push(`${f ? 'podlała' : 'podlał'} ${h.water} ${plural(h.water, 'pole', 'pola', 'pól')}`);
    if (h.feed) parts.push(`${f ? 'nakarmiła' : 'nakarmił'} ${h.feed} ${plural(h.feed, 'zwierzę', 'zwierzęta', 'zwierząt')}`);
    if (h.pet) parts.push(`${f ? 'pogłaskała' : 'pogłaskał'} ${h.pet} ${plural(h.pet, 'zwierzę', 'zwierzęta', 'zwierząt')}`);
    return `${h.name} Ci ${f ? 'pomogła' : 'pomógł'}: ${parts.join(', ')} {heart}`;
  });
}

// Taps become requests one per target; in-flight ones count against the helps left. One walk at a
// time: a tap while Natalka is still walking to a target is ignored (final review: replacing the walk
// left the first help "in flight" forever and the counter one short).
// A Konewka pour reserves the helps it will use (`n`), so the counter never promises more than the server has.
export function pendingGuard() {
  const busy = new Map();                         // key -> helps reserved
  let walking = false;
  const size = () => [...busy.values()].reduce((s, n) => s + n, 0);
  return {
    start(key, helpsLeft, n = 1) {
      if (walking || busy.has(key) || size() + n > helpsLeft) return false;
      busy.set(key, n); walking = true; return true;
    },
    arrived() { walking = false; },
    done(key) { busy.delete(key); walking = false; },
    get walking() { return walking; },
    get size() { return size(); },
  };
}

// The crops helped today, to mark on the visitor's farm (a drop).
export const helpedMarks = (view) => (view?.helped_today?.cells ?? []).map((k) => { const [x, y] = k.split(',').map(Number); return { x, y }; });

// Helping can level the helper up (farm.add_xp): announce it on the friend's farm.
// A help can level the helper up: the level-up card of their own farm (FarmPopups), owner 2026-10-27.
export const levelUps = (events) => (events ?? []).filter((e) => e?.type === 'level_up');

// The drops to pop up now: helped crops not shown yet (each shows once, then fades — owner 2026-09-30).
export function newMarks(view, shown) {
  return helpedMarks(view).filter(({ x, y }) => { const k = `${x},${y}`; if (shown.has(k)) return false; shown.add(k); return true; });
}
